// /opt/dashboard/frontend/src/pages/TTSStudio.js
// 🎙️ TTS Stúdió — OpenAI TTS a backend proxyn keresztül (/api/tts).
// Egyedi generálás + welcome/demo batch (14 nyelv), zip letöltéssel.
import React, { useEffect, useRef, useState } from 'react';
import JSZip from 'jszip';

const C = {
  bg: '#1a1d27', panel: '#22252f', panel2: '#2a2e3a', border: '#2a2e3a',
  text: '#e0e6ed', muted: '#8892a0', accent: '#00d4ff',
  ok: '#22c55e', warn: '#fbbf24', bad: '#ef4444', purple: '#8b5cf6',
};

const languages = [
  { id: 'hu', flag: '🇭🇺', name: 'Magyar', voice: 'nova' },
  { id: 'en', flag: '🇬🇧', name: 'English', voice: 'alloy' },
  { id: 'de', flag: '🇩🇪', name: 'Deutsch', voice: 'onyx' },
  { id: 'fr', flag: '🇫🇷', name: 'Français', voice: 'shimmer' },
  { id: 'it', flag: '🇮🇹', name: 'Italiano', voice: 'alloy' },
  { id: 'es', flag: '🇪🇸', name: 'Español', voice: 'nova' },
  { id: 'pl', flag: '🇵🇱', name: 'Polski', voice: 'onyx' },
  { id: 'zh', flag: '🇨🇳', name: '中文', voice: 'nova' },
  { id: 'ja', flag: '🇯🇵', name: '日本語', voice: 'nova' },
  { id: 'pt', flag: '🇵🇹', name: 'Português', voice: 'shimmer' },
  { id: 'ko', flag: '🇰🇷', name: '한국어', voice: 'nova' },
  { id: 'sk', flag: '🇸🇰', name: 'Slovenčina', voice: 'onyx' },
  { id: 'ro', flag: '🇷🇴', name: 'Română', voice: 'alloy' },
  { id: 'sl', flag: '🇸🇮', name: 'Slovenščina', voice: 'onyx' },
];

// Hang-leírások. Hogy melyik hang ÉRHETŐ EL, azt a modell dönti el — azt a
// szerver /status válaszából vesszük, itt csak a magyar megnevezés van.
// A marin és a cedar a legújabb, az OpenAI ezeket ajánlja a legjobb minőséghez.
const VOICE_INFO = {
  nova: { name: 'Nova', desc: 'Meleg, női' },
  shimmer: { name: 'Shimmer', desc: 'Lágy, női' },
  alloy: { name: 'Alloy', desc: 'Semleges' },
  echo: { name: 'Echo', desc: 'Mély, férfi' },
  onyx: { name: 'Onyx', desc: 'Erős, férfi' },
  fable: { name: 'Fable', desc: 'Mesélő' },
  ash: { name: 'Ash', desc: 'Tiszta, artikulált' },
  coral: { name: 'Coral', desc: 'Élénk, meleg' },
  sage: { name: 'Sage', desc: 'Nyugodt, megfontolt' },
  ballad: { name: 'Ballad', desc: 'Lágy, dallamos' },
  verse: { name: 'Verse', desc: 'Sokoldalú, kifejező' },
  marin: { name: 'Marin', desc: 'Friss, könnyed', best: true },
  cedar: { name: 'Cedar', desc: 'Meleg, megtartó', best: true },
};

const MODEL_CARDS = [
  { id: 'tts-1', sub: 'gyors · olcsó' },
  { id: 'tts-1-hd', sub: 'jobb minőség' },
  { id: 'gpt-4o-mini-tts', sub: 'irányítható · új' },
];

const AUDIO_FORMATS = [
  { id: 'mp3', label: 'MP3', hint: 'tömörített, appba ez kell' },
  { id: 'wav', label: 'WAV', hint: 'veszteségmentes' },
  { id: 'flac', label: 'FLAC', hint: 'veszteségmentes, tömörített' },
  { id: 'opus', label: 'Opus', hint: 'kis méret, streaming' },
  { id: 'aac', label: 'AAC', hint: 'Apple-barát' },
];

// Az utasítás ANGOLUL a legmegbízhatóbb (az OpenAI példái is azok) — ez NEM
// befolyásolja a felolvasott szöveg nyelvét, csak az előadásmódot.
const INSTRUCTION_PRESETS = [
  { label: '🧭 Idegenvezető', text: 'Speak like a warm, experienced tour guide: calm pace, clear articulation, friendly tone. Pause briefly between sentences and gently emphasise dates and names.' },
  { label: '🌙 Meghitt, esti', text: 'Speak softly and intimately, as if telling a story on an evening walk. Slower pace, gentle delivery, low volume.' },
  { label: '✨ Lelkes', text: 'Speak with genuine enthusiasm and energy, like someone excited to share this story. Lively intonation, but never rushed.' },
  { label: '🎬 Dokumentumfilm', text: 'Speak like a serious documentary narrator. Measured pace, authoritative tone, restrained emotion, crisp consonants.' },
  { label: '⚡ Gyorsabb tempó', text: 'Speak somewhat faster than usual while staying clearly articulated and easy to follow.' },
];

// Költségbecslés modellenként.
//   tts-1 / tts-1-hd: KARAKTER alapú — $15, illetve $30 / 1M karakter.
//   gpt-4o-mini-tts: TOKEN alapú — a bemenő szöveg ($0.60/1M token) plusz a
//     generált hang ($12/1M audio token, ami ~$0.015 percenként). A hang
//     hosszát a karakterszámból becsüljük (~900 karakter ≈ 1 perc beszéd),
//     ezért ez közelítés, nem számla.
const CHARS_PER_MINUTE = 900;
function estimateCost(chars, model) {
  if (model === 'gpt-4o-mini-tts') {
    const inputUsd = (chars / 4 / 1e6) * 0.6;
    const audioUsd = (chars / CHARS_PER_MINUTE) * 0.015;
    return { usd: inputUsd + audioUsd, approx: true };
  }
  return { usd: (chars / 1000) * (model === 'tts-1-hd' ? 0.03 : 0.015), approx: false };
}

const welcomeTexts = {
  hu: "Üdv, TourMate vagyok! A térképen körülötted látod a közeli érdekes helyeket. Indulj el, és mesélek mindenről amellett elhaladsz, vagy érintsd meg az ikonokat egy gyors történetért. Ha most nincs a közeledben látnivaló, semmi gond — utazz el bárhova, ott is veled leszek.",
  en: "Hi, I'm TourMate! On the map you can see interesting places around you. Start moving and I'll tell you about everything you pass, or tap any icon for a quick story. If there's nothing nearby right now, no worries — travel anywhere and I'll be there with you.",
  de: "Hallo, ich bin TourMate! Auf der Karte siehst du interessante Orte in deiner Nähe. Geh los und ich erzähle dir alles, woran du vorbeikommst, oder tippe auf ein Symbol für eine kurze Geschichte. Wenn gerade nichts in deiner Nähe ist, keine Sorge — reise irgendwohin, und ich bin auch dort bei dir.",
  fr: "Bonjour, je suis TourMate ! Sur la carte, tu vois les lieux intéressants autour de toi. Mets-toi en route et je te raconterai tout ce que tu croiseras, ou touche une icône pour une histoire rapide. S'il n'y a rien à proximité, pas d'inquiétude — voyage n'importe où et je serai aussi avec toi.",
  it: "Ciao, sono TourMate! Sulla mappa vedi i luoghi interessanti intorno a te. Mettiti in cammino e ti racconterò tutto ciò che incontri, oppure tocca un'icona per una storia veloce. Se al momento non c'è nulla nelle vicinanze, nessun problema — viaggia ovunque e sarò lì con te.",
  es: "¡Hola, soy TourMate! En el mapa puedes ver lugares interesantes a tu alrededor. Empieza a moverte y te contaré sobre todo lo que pases, o toca cualquier icono para una historia rápida. Si ahora mismo no hay nada cerca, no te preocupes — viaja a cualquier lugar y estaré contigo allí también.",
  pl: "Cześć, jestem TourMate! Na mapie widzisz ciekawe miejsca wokół ciebie. Ruszaj w drogę, a opowiem ci o wszystkim, co mijasz, albo dotknij dowolnej ikony, by usłyszeć szybką historię. Jeśli teraz nic nie ma w pobliżu, żaden problem — podróżuj gdziekolwiek, będę z tobą i tam.",
  zh: "你好,我是 TourMate!在地图上你可以看到周围有趣的地方。出发吧,我会告诉你所有经过的地方,或者点击任何图标听一个简短的故事。如果现在附近没有,别担心——你去哪里我都会陪着你。",
  ja: "こんにちは、TourMateです!地図には周りの興味深い場所が表示されています。歩き出してください、通り過ぎるすべての場所についてお話しします。アイコンをタップすれば、短いストーリーをすぐに聞けます。今近くに何もなくても大丈夫——どこに旅しても、私はあなたと一緒です。",
  pt: "Olá, sou o TourMate! No mapa você vê lugares interessantes ao seu redor. Comece a se mover e eu te contarei sobre tudo que passar, ou toque em qualquer ícone para uma história rápida. Se não há nada por perto agora, sem problema — viaje para qualquer lugar e estarei com você lá também.",
  ko: "안녕하세요, TourMate입니다! 지도에서 주변의 흥미로운 장소들을 볼 수 있어요. 출발하시면 지나는 모든 곳에 대해 이야기해드리고, 아이콘을 누르면 짧은 이야기를 들으실 수 있어요. 지금 근처에 아무것도 없어도 괜찮아요 — 어디든 여행하시면 저도 함께 있을 거예요.",
  sk: "Ahoj, som TourMate! Na mape vidíš zaujímavé miesta okolo seba. Vydaj sa na cestu a poviem ti o všetkom, čo prejdeš, alebo klepni na ikonu pre rýchly príbeh. Ak práve nič nie je nablízku, žiaden problém — cestuj kamkoľvek a budem s tebou aj tam.",
  ro: "Salut, sunt TourMate! Pe hartă vezi locurile interesante din jurul tău. Pornește la drum și îți voi povesti despre tot ce întâlnești, sau atinge o pictogramă pentru o poveste rapidă. Dacă acum nu este nimic în apropiere, nicio problemă — călătorește oriunde și voi fi acolo cu tine.",
  sl: "Pozdravljen, jaz sem TourMate! Na zemljevidu vidiš zanimive kraje okoli sebe. Pojdi na pot in povedal ti bom o vsem, kar boš srečal, ali pa se dotakni katere koli ikone za hitro zgodbo. Če zdaj ni ničesar v bližini, brez skrbi — potuj kamor koli in tam bom s teboj."
};

const demoTexts = {
  "hu": "Az Eiffel-torony előtt állsz — háromszázharminc méternyi vascsipke, amely a párizsi égbe nyúlik. Amikor Gustave Eiffel a világkiállításra felavatta, sok párizsit megdöbbentett. Művészek és írók tiltakozó leveleket írtak alá, szörnyű szemfájdalomnak nevezve, és eredetileg csak húsz évig állhatott volna, mielőtt lebontják. Mi mentette meg? A rádió. A torony tökéletes antennának bizonyult, és a „haszontalan” óriás egyik napról a másikra nélkülözhetetlenné vált. Ha jól megnézed, három árnyalatra van festve — lent sötétebb, fent világosabb —, optikai trükk, amitől az ég előtt egyetlen, egyenletes színűnek tűnik. Meleg napon a vas kitágul, és az egész torony körülbelül tizenöt centiméterrel magasabbra nő, majd éjszakára újra összehúzódik. És minden este, óránként, húszezer aranyszínű fény gyúl ki egy ötperces csillogásra. Évente körülbelül hétmillióan látogatják — Gustave Eiffel mégis fenntartott magának egy apró lakást a csúcs közelében, és köztudottan semmi pénzért nem adta bérbe. És ez csak egyetlen hely. Képzeld el, hogy ilyen történet vár rád mindenhol, amerre jársz.",
  "en": "You're standing before the Eiffel Tower — three hundred and thirty metres of iron lace reaching into the Paris sky. When Gustave Eiffel unveiled it for the World's Fair, many Parisians were horrified. Artists and writers signed petitions calling it a monstrous eyesore, and it was only meant to stand for twenty years before being torn down. So what saved it? Radio. The tower turned out to be a perfect antenna, and overnight the 'useless' giant became indispensable. Look closely and you'll see it's painted in three shades — darker at the bottom, lighter at the top — an optical trick that makes it look like one even colour against the sky. On a hot day the iron expands and the whole tower grows about fifteen centimetres taller, then shrinks again by night. And every evening, on the hour, twenty thousand golden lights burst into a five-minute sparkle. Around seven million people visit each year — yet Gustave Eiffel kept a tiny private apartment near the very top, and famously refused to rent it out, no matter the offer. And that's just one place. Imagine a story like this waiting everywhere you go.",
  "de": "Du stehst vor dem Eiffelturm — dreihundertdreißig Meter eiserne Spitze, die in den Pariser Himmel ragt. Als Gustave Eiffel ihn zur Weltausstellung enthüllte, waren viele Pariser entsetzt. Künstler und Schriftsteller unterschrieben Petitionen und nannten ihn einen monströsen Schandfleck, und eigentlich sollte er nur zwanzig Jahre stehen, bevor man ihn abreißt. Was hat ihn gerettet? Das Radio. Der Turm erwies sich als perfekte Antenne, und über Nacht wurde der „nutzlose“ Riese unentbehrlich. Sieh genau hin: Er ist in drei Farbtönen gestrichen — unten dunkler, oben heller —, ein optischer Trick, durch den er vor dem Himmel wie eine einzige, gleichmäßige Farbe wirkt. An einem heißen Tag dehnt sich das Eisen aus, und der ganze Turm wächst um etwa fünfzehn Zentimeter, um nachts wieder zu schrumpfen. Und jeden Abend, zur vollen Stunde, sprühen zwanzigtausend goldene Lichter fünf Minuten lang Funken. Rund sieben Millionen Menschen besuchen ihn jährlich — und doch behielt Gustave Eiffel eine winzige Privatwohnung nahe der Spitze und weigerte sich bekanntlich, sie zu vermieten, egal zu welchem Preis. Und das ist nur ein einziger Ort. Stell dir vor, überall, wohin du gehst, wartet so eine Geschichte.",
  "fr": "Tu te trouves devant la tour Eiffel — trois cent trente mètres de dentelle de fer qui s'élancent dans le ciel de Paris. Quand Gustave Eiffel l'a dévoilée pour l'Exposition universelle, beaucoup de Parisiens étaient horrifiés. Des artistes et des écrivains ont signé des pétitions, la qualifiant de monstruosité, et elle ne devait rester debout que vingt ans avant d'être démolie. Qu'est-ce qui l'a sauvée ? La radio. La tour s'est révélée être une antenne parfaite, et du jour au lendemain, le géant « inutile » est devenu indispensable. Regarde bien : elle est peinte en trois nuances — plus foncée en bas, plus claire en haut —, une illusion d'optique qui la fait paraître d'une seule couleur uniforme contre le ciel. Par temps chaud, le fer se dilate et la tour entière grandit d'environ quinze centimètres, avant de rétrécir la nuit. Et chaque soir, à l'heure pile, vingt mille lumières dorées scintillent pendant cinq minutes. Environ sept millions de personnes la visitent chaque année — pourtant Gustave Eiffel gardait un petit appartement privé près du sommet, et il refusait, dit-on, de le louer à n'importe quel prix. Et ce n'est qu'un seul endroit. Imagine une histoire pareille qui t'attend partout où tu vas.",
  "it": "Sei davanti alla Torre Eiffel — trecentotrenta metri di merletto di ferro che si slanciano nel cielo di Parigi. Quando Gustave Eiffel la presentò per l'Esposizione universale, molti parigini rimasero inorriditi. Artisti e scrittori firmarono petizioni, definendola un mostruoso pugno nell'occhio, e doveva restare in piedi solo vent'anni prima di essere demolita. Cosa la salvò? La radio. La torre si rivelò un'antenna perfetta e, da un giorno all'altro, il gigante «inutile» divenne indispensabile. Guarda bene: è dipinta in tre tonalità — più scura in basso, più chiara in alto —, un trucco ottico che la fa sembrare di un unico colore uniforme contro il cielo. In una giornata calda il ferro si dilata e l'intera torre cresce di circa quindici centimetri, per poi restringersi di notte. E ogni sera, allo scoccare dell'ora, ventimila luci dorate brillano per cinque minuti. Circa sette milioni di persone la visitano ogni anno — eppure Gustave Eiffel teneva un piccolo appartamento privato vicino alla cima, e si rifiutava notoriamente di affittarlo a qualsiasi prezzo. E questo è solo un luogo. Immagina una storia così ad aspettarti ovunque tu vada.",
  "es": "Estás frente a la Torre Eiffel — trescientos treinta metros de encaje de hierro que se elevan hacia el cielo de París. Cuando Gustave Eiffel la presentó para la Exposición Universal, muchos parisinos quedaron horrorizados. Artistas y escritores firmaron peticiones llamándola un monstruoso adefesio, y solo debía permanecer en pie veinte años antes de ser derribada. ¿Qué la salvó? La radio. La torre resultó ser una antena perfecta y, de la noche a la mañana, el gigante «inútil» se volvió indispensable. Fíjate bien: está pintada en tres tonos — más oscuro abajo, más claro arriba —, un truco óptico que la hace parecer de un solo color uniforme contra el cielo. En un día caluroso el hierro se dilata y toda la torre crece unos quince centímetros, para volver a encogerse de noche. Y cada noche, en punto, veinte mil luces doradas destellan durante cinco minutos. Unos siete millones de personas la visitan cada año — y sin embargo Gustave Eiffel guardaba un pequeño apartamento privado cerca de la cima, y se negaba, según se cuenta, a alquilarlo a cualquier precio. Y esto es solo un lugar. Imagina una historia así esperándote dondequiera que vayas.",
  "pl": "Stoisz przed wieżą Eiffla — trzysta trzydzieści metrów żelaznej koronki wznoszącej się w paryskie niebo. Gdy Gustave Eiffel odsłonił ją na Wystawę Światową, wielu paryżan było przerażonych. Artyści i pisarze podpisywali petycje, nazywając ją potwornym szpetnym tworem, a miała stać tylko dwadzieścia lat, zanim zostanie rozebrana. Co ją ocaliło? Radio. Wieża okazała się idealną anteną i z dnia na dzień „bezużyteczny” olbrzym stał się niezastąpiony. Przyjrzyj się: pomalowano ją w trzech odcieniach — ciemniejszym na dole, jaśniejszym na górze — to optyczna sztuczka, dzięki której na tle nieba wygląda na jeden, równy kolor. W upalny dzień żelazo się rozszerza i cała wieża rośnie o około piętnaście centymetrów, by w nocy znów się skurczyć. A każdego wieczoru, o pełnej godzinie, dwadzieścia tysięcy złotych świateł migocze przez pięć minut. Rocznie odwiedza ją około siedmiu milionów ludzi — a mimo to Gustave Eiffel miał na samej górze maleńkie prywatne mieszkanie i podobno odmawiał wynajęcia go za jakąkolwiek cenę. A to tylko jedno miejsce. Wyobraź sobie, że taka historia czeka na ciebie wszędzie, dokądkolwiek pójdziesz.",
  "zh": "你正站在埃菲尔铁塔前——三百三十米高的钢铁花边,直插巴黎的天空。当居斯塔夫·埃菲尔为世界博览会揭幕时,许多巴黎人感到震惊。艺术家和作家们联名抗议,称它是丑陋的怪物,而它原本只打算矗立二十年便拆除。是什么救了它?无线电。这座塔被证明是绝佳的天线,一夜之间,这个“无用”的巨人变得不可或缺。仔细看,它被漆成三种色调——下深上浅——这是一种视觉把戏,让它在天空映衬下看起来是均匀的一种颜色。炎热的日子里,钢铁会膨胀,整座塔会长高约十五厘米,夜里又会收缩。每天傍晚整点,两万盏金色灯光会闪耀五分钟。每年约有七百万人前来参观——然而居斯塔夫·埃菲尔在塔顶附近保留了一间小小的私人公寓,据说无论出价多少他都拒绝出租。而这仅仅是一个地方。想象一下,你所到之处,处处都有这样的故事在等着你。",
  "ja": "あなたは今、エッフェル塔の前に立っています——三百三十メートルの鉄のレースが、パリの空へと伸びています。ギュスターヴ・エッフェルが万国博覧会のためにこれを公開したとき、多くのパリ市民は衝撃を受けました。芸術家や作家たちは「醜い怪物だ」と請願書に署名し、本来は二十年だけ立って、その後は取り壊される予定でした。では、何がこの塔を救ったのでしょう?ラジオです。塔は完璧なアンテナであることがわかり、「役立たず」の巨人は一夜にしてなくてはならない存在になりました。よく見ると、三つの色合いで塗られています——下は濃く、上は淡く——空を背景に一つの均一な色に見えるようにする、目の錯覚を使った工夫です。暑い日には鉄が膨張し、塔全体が約十五センチ高くなり、夜になるとまた縮みます。そして毎晩、毎正時に、二万個の金色の光が五分間きらめきます。年間およそ七百万人が訪れます——それでもギュスターヴ・エッフェルは塔の頂上近くに小さな自分の部屋を持っていて、いくら積まれても貸さなかったと言われています。これはほんの一つの場所にすぎません。あなたが行く先々で、こんな物語が待っていると想像してみてください。",
  "pt": "Estás diante da Torre Eiffel — trezentos e trinta metros de renda de ferro que se erguem no céu de Paris. Quando Gustave Eiffel a revelou para a Exposição Universal, muitos parisienses ficaram horrorizados. Artistas e escritores assinaram petições, chamando-a de monstruosidade, e ela só deveria ficar de pé vinte anos antes de ser demolida. O que a salvou? O rádio. A torre revelou-se uma antena perfeita e, de um dia para o outro, o gigante «inútil» tornou-se indispensável. Repara bem: está pintada em três tons — mais escuro em baixo, mais claro em cima —, um truque ótico que a faz parecer de uma só cor uniforme contra o céu. Num dia quente, o ferro dilata-se e a torre inteira cresce cerca de quinze centímetros, voltando a encolher à noite. E todas as noites, à hora certa, vinte mil luzes douradas brilham durante cinco minutos. Cerca de sete milhões de pessoas visitam-na todos os anos — e, ainda assim, Gustave Eiffel mantinha um pequeno apartamento privado perto do topo, e recusava-se, segundo se conta, a alugá-lo por qualquer preço. E isto é apenas um lugar. Imagina uma história destas à tua espera por onde quer que vás.",
  "ko": "당신은 지금 에펠탑 앞에 서 있습니다 — 파리의 하늘로 솟아오른 삼백삼십 미터의 철 레이스죠. 귀스타브 에펠이 만국박람회를 위해 이 탑을 공개했을 때, 많은 파리 시민들은 충격을 받았습니다. 예술가와 작가들은 흉측한 괴물이라며 청원서에 서명했고, 원래는 이십 년만 서 있다가 철거될 예정이었습니다. 무엇이 이 탑을 구했을까요? 바로 라디오입니다. 이 탑은 완벽한 안테나로 밝혀졌고, 하룻밤 사이에 '쓸모없던' 거인은 없어서는 안 될 존재가 되었습니다. 자세히 보면 세 가지 색조로 칠해져 있어요 — 아래는 더 진하고, 위는 더 옅게 — 하늘을 배경으로 하나의 균일한 색처럼 보이게 하는 시각적 장치입니다. 더운 날에는 철이 팽창해 탑 전체가 약 십오 센티미터 더 자랐다가, 밤이 되면 다시 줄어듭니다. 그리고 매일 저녁 정각마다, 이만 개의 황금빛 조명이 오 분 동안 반짝입니다. 매년 약 칠백만 명이 찾아오죠 — 그런데도 귀스타브 에펠은 꼭대기 근처에 작은 개인 아파트를 두었고, 아무리 큰 돈을 줘도 세를 놓지 않았다고 합니다. 그리고 이건 단 한 곳일 뿐입니다. 당신이 가는 곳마다 이런 이야기가 기다리고 있다고 상상해 보세요.",
  "sk": "Stojíš pred Eiffelovou vežou — tristotridsať metrov železnej čipky týčiacej sa do parížskeho neba. Keď ju Gustave Eiffel odhalil pri príležitosti Svetovej výstavy, mnohí Parížania boli zhrození. Umelci a spisovatelia podpisovali petície a nazývali ju obludným kazom, pričom mala stáť len dvadsať rokov, kým ju zbúrajú. Čo ju zachránilo? Rádio. Veža sa ukázala ako dokonalá anténa a „neužitočný“ obor sa zo dňa na deň stal nepostrádateľným. Pozri sa pozorne: je natretá v troch odtieňoch — dole tmavšom, hore svetlejšom — je to optický trik, vďaka ktorému pôsobí na pozadí oblohy ako jedna rovnomerná farba. V horúci deň sa železo rozpína a celá veža narastie asi o pätnásť centimetrov, aby sa v noci opäť stiahla. A každý večer, na celú hodinu, dvadsaťtisíc zlatých svetiel päť minút iskrí. Ročne ju navštívi približne sedem miliónov ľudí — a predsa si Gustave Eiffel ponechal blízko vrcholu malý súkromný byt a vraj ho odmietal prenajať za akúkoľvek cenu. A toto je len jedno miesto. Predstav si, že taký príbeh ťa čaká všade, kam sa vyberieš.",
  "ro": "Te afli în fața Turnului Eiffel — trei sute treizeci de metri de dantelă de fier care se înalță spre cerul Parisului. Când Gustave Eiffel l-a dezvăluit pentru Expoziția Universală, mulți parizieni au fost îngroziți. Artiști și scriitori au semnat petiții, numindu-l o monstruozitate, iar el trebuia să stea doar douăzeci de ani înainte de a fi demolat. Ce l-a salvat? Radioul. Turnul s-a dovedit a fi o antenă perfectă și, peste noapte, uriașul „inutil” a devenit indispensabil. Privește cu atenție: este vopsit în trei nuanțe — mai închisă jos, mai deschisă sus — un truc optic care îl face să pară de o singură culoare uniformă pe fundalul cerului. Într-o zi călduroasă, fierul se dilată și întregul turn crește cu aproximativ cincisprezece centimetri, apoi se micșorează din nou noaptea. Și în fiecare seară, la ora fixă, douăzeci de mii de lumini aurii sclipesc timp de cinci minute. În jur de șapte milioane de oameni îl vizitează anual — și totuși Gustave Eiffel păstra un mic apartament privat aproape de vârf și, se spune, refuza să-l închirieze indiferent de preț. Și acesta este doar un singur loc. Imaginează-ți o astfel de poveste așteptându-te oriunde mergi.",
  "sl": "Stojiš pred Eifflovim stolpom — tristo trideset metrov železne čipke, ki se vzpenja v pariško nebo. Ko ga je Gustave Eiffel razkril za svetovno razstavo, so bili mnogi Parižani zgroženi. Umetniki in pisatelji so podpisovali peticije in ga imenovali pošastno spako, stati pa naj bi samo dvajset let, preden bi ga podrli. Kaj ga je rešilo? Radio. Stolp se je izkazal za popolno anteno in čez noč je „nekoristni“ velikan postal nepogrešljiv. Poglej natančno: pobarvan je v treh odtenkih — spodaj temnejši, zgoraj svetlejši — optična prevara, zaradi katere se na ozadju neba zdi enakomerne barve. Na vroč dan se železo razširi in celoten stolp zraste za približno petnajst centimetrov, ponoči pa se spet skrči. In vsak večer, ob polni uri, dvajset tisoč zlatih luči pet minut iskri. Letno ga obišče približno sedem milijonov ljudi — pa vendar je Gustave Eiffel blizu vrha obdržal majhno zasebno stanovanje in ga baje ni hotel oddati za nobeno ceno. In to je le en kraj. Predstavljaj si, da te taka zgodba čaka povsod, kamor greš."
};

// === TextHelper (Dart portból) ===
function textHelperOptimize(text, lang) {
  let result = text;
  const roman = {
    I: '1', II: '2', III: '3', IV: '4', V: '5', VI: '6', VII: '7', VIII: '8', IX: '9',
    X: '10', XI: '11', XII: '12', XIII: '13', XIV: '14', XV: '15', XVI: '16', XVII: '17', XVIII: '18', XIX: '19', XX: '20',
  };
  result = result.replace(/\b(XX|XIX|XVIII|XVII|XVI|XV|XIV|XIII|XII|XI|X|IX|VIII|VII|VI|V|IV|III|II|I)\.\s/g,
    (m, r) => (roman[r] || r) + '. ');
  if (lang === 'hu') {
    result = result.replace(/Kr\.\s*e\.\s*/g, 'Krisztus előtt ');
    result = result.replace(/Kr\.\s*u\.\s*/g, 'Krisztus után ');
  }
  result = result.replace(/(\d+)\s*m²/g, '$1 négyzetméter');
  result = result.replace(/(\d+)\s*km²/g, '$1 négyzetkilométer');
  result = result.replace(/(\d+)\s*km\/h/g, '$1 kilométer per óra');
  result = result.replace(/(\d+)\s*km\b/g, '$1 kilométer');
  result = result.replace(/(\d+)\s*cm\b/g, '$1 centiméter');
  result = result.replace(/(\d+)\s*mm\b/g, '$1 milliméter');
  result = result.replace(/(\d+)\s*m\b(?!\w)/g, '$1 méter');
  if (lang === 'hu') {
    result = result.replace(/(\d+)\s*%/g, '$1 százalék');
  }
  result = result.replace(/(\d+)\s*°C/g, '$1 Celsius fok');
  result = result.replace(/(\d+)\s*°/g, '$1 fok');
  return result;
}

async function apiGenerate(text, voice, model, speed, instructions, format) {
  const res = await fetch('/api/tts/generate', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ text, voice, model, speed, instructions, format }),
  });
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try { const j = await res.json(); if (j.error) msg = j.error; } catch (e) { /* */ }
    throw new Error(msg);
  }
  return res.blob();
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

// ————— UI segédek —————
const card = {
  background: C.panel, border: `1px solid ${C.border}`, borderRadius: 12,
  padding: 18, marginBottom: 14,
};
const cardLabel = {
  display: 'block', fontWeight: 700, fontSize: 12, color: C.muted,
  textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 10,
};
const chipBase = {
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 12px',
  background: C.panel2, border: `1px solid ${C.border}`, borderRadius: 8,
  cursor: 'pointer', fontSize: 13, color: C.text, userSelect: 'none',
};

function ProgressBar({ value }) {
  return (
    <div style={{ height: 4, background: C.border, borderRadius: 2, overflow: 'hidden', marginTop: 8 }}>
      <div style={{
        height: '100%', width: `${value}%`, transition: 'width 0.3s',
        background: `linear-gradient(90deg, ${C.accent}, ${C.purple})`,
      }} />
    </div>
  );
}

function BatchCard({ title, subtitle, buttonLabel, accent, state, onRun }) {
  const total = state.total || 14;
  const doneAll = state.done + state.failed;
  return (
    <div style={{ ...card, border: `1px solid ${accent}` }}>
      <span style={{ ...cardLabel, color: accent }}>{title}</span>
      <div style={{ fontSize: 13, color: C.muted, margin: '4px 0 12px', lineHeight: 1.5 }}>{subtitle}</div>
      <button
        onClick={onRun}
        disabled={state.running}
        style={{
          width: '100%', padding: 14, border: 'none', borderRadius: 10, cursor: state.running ? 'not-allowed' : 'pointer',
          background: `linear-gradient(135deg, ${accent}, ${C.purple})`, color: '#fff',
          fontWeight: 700, fontSize: 14, opacity: state.running ? 0.6 : 1,
        }}
      >
        {state.running ? '⏳ Generálás folyamatban…' : buttonLabel}
      </button>
      {(state.running || state.log.length > 0) && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 13, color: C.muted, marginBottom: 4 }}>
            {doneAll} / {total} · {state.current || '—'}
          </div>
          <ProgressBar value={(doneAll / total) * 100} />
          <div style={{
            maxHeight: 160, overflowY: 'auto', marginTop: 10, fontFamily: 'monospace',
            fontSize: 11, background: C.bg, padding: 8, borderRadius: 6,
          }}>
            {state.log.map((l, i) => (
              <div key={i} style={{ color: l.color || C.muted }}>{l.text}</div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const initialBatch = { running: false, done: 0, failed: 0, total: 14, current: '', log: [], failedIds: [] };

export default function TTSStudio() {
  const [configured, setConfigured] = useState(null);
  const [caps, setCaps] = useState(null);          // modell-mátrix a szervertől
  const [selectedLang, setSelectedLang] = useState('hu');
  const [selectedVoice, setSelectedVoice] = useState('nova');
  const [selectedModel, setSelectedModel] = useState('tts-1-hd');
  const [speed, setSpeed] = useState(1.0);
  const [instructions, setInstructions] = useState('');
  const [format, setFormat] = useState('mp3');
  const [text, setText] = useState('');
  const [optimize, setOptimize] = useState(true);
  const [filename, setFilename] = useState('narracio');
  const [genState, setGenState] = useState({ running: false, error: null, resultUrl: null, resultInfo: null });
  const [welcomeState, setWelcomeState] = useState({ ...initialBatch });
  const [demoState, setDemoState] = useState({ ...initialBatch });

  // Sikeres batch fájlok cache-e — retry esetén csak a hibásakat generáljuk újra
  const welcomeBlobs = useRef(new Map());
  const demoBlobs = useRef(new Map());
  const audioUrlRef = useRef(null);

  useEffect(() => {
    fetch('/api/tts/status')
      .then((r) => r.json())
      .then((j) => { setConfigured(!!j.configured); setCaps(j); })
      .catch(() => setConfigured(false));
  }, []);

  // Mit tud a kiválasztott modell? A szerver a forrás; ha még nem jött meg a
  // válasz, egy józan alapértelmezéssel dolgozunk, hogy az oldal ne legyen üres.
  const modelSpec = ((caps && caps.models) || []).find((m) => m.id === selectedModel) || {
    voices: ['alloy', 'ash', 'coral', 'echo', 'fable', 'nova', 'onyx', 'sage', 'shimmer'],
    instructions: selectedModel === 'gpt-4o-mini-tts',
    speed: selectedModel !== 'gpt-4o-mini-tts',
  };

  // Modellváltásnál a nem támogatott hang némán rossz eredményt adna (a szerver
  // nova-ra esne vissza), ezért inkább látható módon visszaállítjuk.
  useEffect(() => {
    if (!modelSpec.voices.includes(selectedVoice)) setSelectedVoice('nova');
  }, [selectedModel, selectedVoice, modelSpec.voices]);

  const chars = text.length;
  const words = text.trim() ? text.trim().split(/\s+/).length : 0;
  const cost = estimateCost(chars, selectedModel);
  const optimized = optimize && text.trim() ? textHelperOptimize(text, selectedLang) : text;
  const showPreview = optimize && text.trim() && optimized !== text;
  const sentInstructions = modelSpec.instructions ? instructions.trim() : '';
  const instructionsIgnored = !modelSpec.instructions && instructions.trim().length > 0;

  const pickLang = (l) => {
    setSelectedLang(l.id);
    setSelectedVoice(l.voice);
  };

  // A batch ára a tényleges szövegekből számolódik, hogy ne avuljon el.
  const batchCost = (texts) => estimateCost(
    languages.filter((l) => texts[l.id]).reduce((s, l) => s + texts[l.id].length, 0),
    selectedModel,
  );

  async function handleGenerate() {
    const raw = text.trim();
    if (!raw || genState.running) return;
    const finalText = optimize ? textHelperOptimize(raw, selectedLang) : raw;
    setGenState({ running: true, error: null, resultUrl: null, resultInfo: null });
    try {
      const blob = await apiGenerate(finalText, selectedVoice, selectedModel, speed, sentInstructions, format);
      if (audioUrlRef.current) URL.revokeObjectURL(audioUrlRef.current);
      const url = URL.createObjectURL(blob);
      audioUrlRef.current = url;
      const fname = `${filename.trim() || 'narracio'}.${format}`;
      downloadBlob(blob, fname);
      const est = estimateCost(finalText.length, selectedModel);
      setGenState({
        running: false, error: null, resultUrl: url,
        resultInfo: {
          blob, fname,
          text: [
            `${finalText.length} karakter`,
            selectedVoice,
            selectedModel,
            modelSpec.speed ? `${speed}×` : null,
            sentInstructions ? '🎭 hangutasítással' : null,
            format.toUpperCase(),
            `${(blob.size / 1024 / 1024).toFixed(2)} MB`,
            `${est.approx ? '≈' : '~'}$${est.usd.toFixed(3)}`,
          ].filter(Boolean).join(' · '),
        },
      });
    } catch (e) {
      setGenState({ running: false, error: e.message, resultUrl: null, resultInfo: null });
    }
  }

  async function runBatch(kind) {
    const texts = kind === 'welcome' ? welcomeTexts : demoTexts;
    const blobs = kind === 'welcome' ? welcomeBlobs.current : demoBlobs.current;
    const setState = kind === 'welcome' ? setWelcomeState : setDemoState;
    const prevFailed = (kind === 'welcome' ? welcomeState : demoState).failedIds;
    const prefix = kind === 'welcome' ? 'welcome' : 'demo';
    const zipName = kind === 'welcome' ? 'welcome_hangok.zip' : 'demo_narraciok.zip';

    // Ha volt hibás előző futásból, csak azokat; különben mindet (blobs reset)
    let tasks = languages
      .filter((l) => texts[l.id])
      .map((l) => ({ lang: l.id, voice: l.voice, text: texts[l.id], filename: `${prefix}_${l.id}.${format}` }));
    const retryOnly = prevFailed.length > 0;
    if (retryOnly) {
      tasks = tasks.filter((t) => prevFailed.includes(t.lang));
    } else {
      blobs.clear();
    }

    const totalAll = languages.filter((l) => texts[l.id]).length;
    setState({ running: true, done: blobs.size, failed: 0, total: totalAll, current: '', log: [], failedIds: [] });

    let failed = 0;
    const failedIds = [];
    for (const task of tasks) {
      setState((s) => ({ ...s, current: `${task.filename} (${task.voice})` }));
      try {
        const blob = await apiGenerate(task.text, task.voice, selectedModel, 1.0, sentInstructions, format);
        blobs.set(task.filename, blob);
        setState((s) => ({
          ...s, done: blobs.size,
          log: [...s.log, { text: `✅ ${task.filename} · ${task.voice} · ${(blob.size / 1024).toFixed(1)} KB`, color: C.ok }],
        }));
      } catch (e) {
        failed += 1;
        failedIds.push(task.lang);
        setState((s) => ({
          ...s, failed,
          log: [...s.log, { text: `❌ ${task.filename} · HIBA: ${e.message}`, color: C.bad }],
        }));
      }
      await new Promise((r) => setTimeout(r, 250));
    }

    // Zip a sikeresekből
    if (blobs.size > 0) {
      setState((s) => ({ ...s, current: '📦 Zip készítése…' }));
      const zip = new JSZip();
      blobs.forEach((blob, name) => zip.file(name, blob));
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      downloadBlob(zipBlob, zipName);
    }

    setState((s) => ({
      ...s,
      running: false,
      failedIds,
      current: failed > 0
        ? `Kész — ${blobs.size} sikeres, ${failed} hiba. Újra gombbal csak a hibásak futnak.`
        : `✅ Kész! Mind a ${blobs.size} fájl a zipben.`,
    }));
  }

  return (
    <div style={{ maxWidth: 720, color: C.text }}>
      <h2 style={{ margin: '0 0 4px' }}>🎙️ TTS Stúdió</h2>
      <div style={{ color: C.muted, fontSize: 13, marginBottom: 18 }}>
        OpenAI TTS — szövegből hangfájl, a kulcs a szerveren marad
      </div>

      {configured === false && (
        <div style={{ ...card, border: `1px solid ${C.bad}`, color: C.bad, fontSize: 13 }}>
          ⚠️ Az OPENAI_API_KEY nincs beállítva a szerveren — a generálás nem fog működni.
          Add hozzá a /opt/dashboard/.env fájlhoz, majd: docker compose up -d --force-recreate dashboard
        </div>
      )}

      {/* Nyelv */}
      <div style={card}>
        <span style={cardLabel}>Nyelv</span>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {languages.map((l) => (
            <span
              key={l.id}
              onClick={() => pickLang(l)}
              style={{
                ...chipBase,
                background: selectedLang === l.id ? C.accent : C.panel2,
                borderColor: selectedLang === l.id ? C.accent : C.border,
                color: selectedLang === l.id ? '#0a0e1a' : C.text,
                fontWeight: selectedLang === l.id ? 700 : 400,
              }}
            >
              <span>{l.flag}</span>{l.name}
            </span>
          ))}
        </div>
      </div>

      {/* Batch kártyák */}
      <BatchCard
        title="🎙️ Welcome bevezető hangok"
        subtitle={<span>14 nyelv × 1 = <strong>14 fájl</strong> egy zip-ben (welcome_&#123;lang&#125;.{format}). A térkép betöltésekor lejátszódó üzenet, nyelvenként a megfelelő narrátorral. {selectedModel}: {batchCost(welcomeTexts).approx ? '≈' : '~'}${batchCost(welcomeTexts).usd.toFixed(2)}.</span>}
        buttonLabel={welcomeState.failedIds.length > 0 ? `🔁 Sikertelenek újra (${welcomeState.failedIds.length})` : '🚀 Mind a 14 welcome → zip'}
        accent={C.accent}
        state={welcomeState}
        onRun={() => runBatch('welcome')}
      />
      <BatchCard
        title="🎧 Wow demo narrációk (Eiffel-torony)"
        subtitle={<span>14 nyelv × 1 = <strong>14 fájl</strong> egy zip-ben (demo_&#123;lang&#125;.{format}). Az első indításkor lejátszható demo, GPS és kredit nélkül. {selectedModel}: {batchCost(demoTexts).approx ? '≈' : '~'}${batchCost(demoTexts).usd.toFixed(2)}.</span>}
        buttonLabel={demoState.failedIds.length > 0 ? `🔁 Sikertelenek újra (${demoState.failedIds.length})` : '🚀 Mind a 14 demo → zip'}
        accent={C.ok}
        state={demoState}
        onRun={() => runBatch('demo')}
      />

      {/* Szöveg */}
      <div style={card}>
        <span style={cardLabel}>Szöveg</span>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Írd ide a narrálni kívánt szöveget…"
          style={{
            width: '100%', minHeight: 140, background: C.panel2, border: `1px solid ${C.border}`,
            borderRadius: 10, padding: 14, color: C.text, fontSize: 14, lineHeight: 1.5,
            resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit',
          }}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: 12, color: C.muted }}>
          <span>
            {chars} karakter · {cost.approx ? '≈' : '~'}${cost.usd.toFixed(3)}
            {cost.approx && <span style={{ opacity: 0.7 }}> (becslés)</span>}
            {chars > 4096 ? ' · ⚠️ max 4096!' : ''}
          </span>
          <span>{words} szó</span>
        </div>
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginTop: 12, paddingTop: 12, borderTop: `1px solid ${C.border}`,
        }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 500 }}>TextHelper előfeldolgozás</div>
            <div style={{ fontSize: 11, color: C.muted }}>Számok, mértékegységek, római számok → kimondható szavak</div>
          </div>
          <div
            onClick={() => setOptimize(!optimize)}
            style={{
              width: 44, height: 24, borderRadius: 12, cursor: 'pointer', position: 'relative',
              background: optimize ? C.accent : C.border, transition: 'background 0.2s', flexShrink: 0,
            }}
          >
            <div style={{
              position: 'absolute', width: 18, height: 18, borderRadius: '50%', top: 3,
              left: optimize ? 23 : 3, background: optimize ? '#fff' : C.muted, transition: 'all 0.2s',
            }} />
          </div>
        </div>
        {showPreview && (
          <div style={{
            marginTop: 10, padding: 10, background: C.panel2, borderRadius: 8,
            fontSize: 13, color: C.warn, lineHeight: 1.5, maxHeight: 100, overflowY: 'auto',
          }}>
            {optimized}
          </div>
        )}
      </div>

      {/* Hang */}
      <div style={card}>
        <span style={cardLabel}>Narrátor hang</span>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
          {modelSpec.voices.map((id) => {
            const v = VOICE_INFO[id] || { name: id, desc: 'új hang' };
            const on = selectedVoice === id;
            return (
              <div
                key={id}
                onClick={() => setSelectedVoice(id)}
                style={{
                  padding: '10px 8px', borderRadius: 10, cursor: 'pointer', textAlign: 'center',
                  background: on ? C.accent : C.panel2,
                  border: `1px solid ${on ? C.accent : (v.best ? C.purple : C.border)}`,
                  color: on ? '#0a0e1a' : C.text,
                }}
              >
                <div style={{ fontWeight: 700, fontSize: 14 }}>
                  {v.name}{v.best && <span title="Az OpenAI ezt ajánlja a legjobb minőséghez"> ⭐</span>}
                </div>
                <div style={{ fontSize: 11, opacity: 0.75 }}>{v.desc}</div>
              </div>
            );
          })}
        </div>
        {selectedModel === 'gpt-4o-mini-tts' && (
          <div style={{ fontSize: 11, color: C.muted, marginTop: 10, lineHeight: 1.5 }}>
            ⭐ = az OpenAI szerint ezek adják a legjobb minőséget. A <strong>marin</strong> és a{' '}
            <strong>cedar</strong> csak ennél a modellnél érhető el.
          </div>
        )}
      </div>

      {/* Modell + sebesség + fájlnév */}
      <div style={card}>
        <span style={cardLabel}>Modell</span>
        <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
          {MODEL_CARDS.map((m) => (
            <div
              key={m.id}
              onClick={() => setSelectedModel(m.id)}
              style={{
                flex: 1, padding: 10, borderRadius: 10, cursor: 'pointer', textAlign: 'center',
                background: selectedModel === m.id ? C.accent : C.panel2,
                border: `1px solid ${selectedModel === m.id ? C.accent : C.border}`,
                color: selectedModel === m.id ? '#0a0e1a' : C.text, fontWeight: 700, fontSize: 13,
              }}
            >
              {m.id}<br />
              <span style={{ fontSize: 11, fontWeight: 400, opacity: 0.75 }}>{m.sub}</span>
            </div>
          ))}
        </div>

        {/* 🆕 Hangvezérlés — csak a gpt-4o-mini-tts érti. Ez a modell fő újdonsága:
            szabad szöveggel lehet kérni hangnemet, tempót, hangsúlyt. */}
        {modelSpec.instructions && (
          <>
            <span style={cardLabel}>Hangutasítás (instructions)</span>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
              {INSTRUCTION_PRESETS.map((p) => (
                <span
                  key={p.label}
                  onClick={() => setInstructions(p.text)}
                  style={{ ...chipBase, fontSize: 12, padding: '5px 10px' }}
                >
                  {p.label}
                </span>
              ))}
              {instructions && (
                <span
                  onClick={() => setInstructions('')}
                  style={{ ...chipBase, fontSize: 12, padding: '5px 10px', color: C.bad, borderColor: C.bad }}
                >
                  ✕ Törlés
                </span>
              )}
            </div>
            <textarea
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              placeholder="Pl.: Speak like a warm, experienced tour guide: calm pace, clear articulation…"
              style={{
                width: '100%', minHeight: 70, background: C.panel2, border: `1px solid ${C.border}`,
                borderRadius: 10, padding: 12, color: C.text, fontSize: 13, lineHeight: 1.5,
                resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit',
              }}
            />
            <div style={{ fontSize: 11, color: C.muted, margin: '6px 0 16px', lineHeight: 1.5 }}>
              Az előadásmódot állítja: hangnem, tempó, hangsúly, akcentus. Angolul a
              legmegbízhatóbb — a <strong>felolvasott szöveg nyelvét ez nem változtatja meg</strong>.
              {instructions.length > 0 && ` · ${instructions.length} karakter`}
            </div>
          </>
        )}
        {instructionsIgnored && (
          <div style={{
            fontSize: 12, color: C.warn, background: C.panel2, borderRadius: 8,
            padding: '8px 12px', marginBottom: 16, lineHeight: 1.5,
          }}>
            ⚠️ Van beírt hangutasításod, de a(z) <strong>{selectedModel}</strong> nem támogatja —
            most <strong>nem megy el</strong>. Válts <strong>gpt-4o-mini-tts</strong>-re, ha használni akarod.
          </div>
        )}

        <span style={cardLabel}>Sebesség</span>
        {modelSpec.speed ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <span style={{ fontSize: 12, color: C.muted }}>0.25×</span>
            <input
              type="range" min="0.25" max="4.0" step="0.05" value={speed}
              onChange={(e) => setSpeed(parseFloat(e.target.value))}
              style={{ flex: 1, accentColor: C.accent }}
            />
            <span style={{ fontSize: 12, color: C.muted }}>4.0×</span>
            <span style={{ fontFamily: 'monospace', fontSize: 14, minWidth: 48, textAlign: 'center' }}>
              {speed.toFixed(2)}×
            </span>
          </div>
        ) : (
          // A gpt-4o-mini-tts elfogadja a speed paramétert, de nem hat rá — ezért
          // el sem küldjük, és a csúszkát sem mutatjuk működőnek.
          <div style={{
            fontSize: 12, color: C.muted, background: C.panel2, borderRadius: 8,
            padding: '10px 12px', marginBottom: 16, lineHeight: 1.5,
          }}>
            A <strong>{selectedModel}</strong> figyelmen kívül hagyja a sebesség-csúszkát,
            ezért el sem küldjük. A tempót a <strong>hangutasításban</strong> kérd — például:
            <em> „Speak somewhat faster than usual…”</em>
          </div>
        )}

        <span style={cardLabel}>Formátum</span>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
          {AUDIO_FORMATS.map((f) => (
            <span
              key={f.id}
              onClick={() => setFormat(f.id)}
              title={f.hint}
              style={{
                ...chipBase, fontSize: 12,
                background: format === f.id ? C.accent : C.panel2,
                borderColor: format === f.id ? C.accent : C.border,
                color: format === f.id ? '#0a0e1a' : C.text,
                fontWeight: format === f.id ? 700 : 400,
              }}
            >
              {f.label}
            </span>
          ))}
          <span style={{ fontSize: 11, color: C.muted, alignSelf: 'center' }}>
            {(AUDIO_FORMATS.find((f) => f.id === format) || {}).hint}
          </span>
        </div>

        <span style={cardLabel}>Fájlnév</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <input
            type="text" value={filename} onChange={(e) => setFilename(e.target.value)}
            style={{
              flex: 1, background: C.panel2, border: `1px solid ${C.border}`, borderRadius: 8,
              padding: '10px 14px', color: C.text, fontFamily: 'monospace', fontSize: 14,
            }}
          />
          <span style={{ color: C.muted, fontFamily: 'monospace', fontSize: 14 }}>.{format}</span>
        </div>
      </div>

      {/* Generálás */}
      <button
        onClick={handleGenerate}
        disabled={genState.running || !text.trim() || chars > 4096}
        style={{
          width: '100%', padding: 16, border: 'none', borderRadius: 12, cursor: 'pointer',
          background: `linear-gradient(135deg, ${C.accent}, ${C.purple})`, color: '#fff',
          fontWeight: 700, fontSize: 16,
          opacity: genState.running || !text.trim() || chars > 4096 ? 0.5 : 1,
        }}
      >
        {genState.running ? '⏳ Generálás…' : '🎙️ Generálás és letöltés'}
      </button>

      {genState.error && (
        <div style={{ ...card, marginTop: 14, border: `1px solid ${C.bad}` }}>
          <div style={{ fontWeight: 700, color: C.bad, marginBottom: 4 }}>❌ Hiba</div>
          <div style={{ fontSize: 13, color: C.muted }}>{genState.error}</div>
        </div>
      )}
      {genState.resultUrl && genState.resultInfo && (
        <div style={{ ...card, marginTop: 14, border: `1px solid ${C.ok}` }}>
          <div style={{ fontWeight: 700, color: C.ok, marginBottom: 6 }}>✅ Kész!</div>
          <div style={{ fontSize: 13, color: C.muted, marginBottom: 10 }}>{genState.resultInfo.text}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <audio controls src={genState.resultUrl} style={{ flex: 1, height: 36 }} />
            <button
              onClick={() => downloadBlob(genState.resultInfo.blob, genState.resultInfo.fname)}
              style={{
                background: C.ok, border: 'none', borderRadius: 8, color: '#fff',
                padding: '8px 16px', fontWeight: 700, fontSize: 13, cursor: 'pointer',
              }}
            >
              💾 Letöltés újra
            </button>
          </div>
        </div>
      )}
      <div style={{ height: 40 }} />
    </div>
  );
}
